const TENANT_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const TENANT_PREFERENCE_STORAGE_KEY = "osirisbrain-tenant-id";
export const PENDING_TENANT_RNC_STORAGE_KEY = "osirisbrain-pending-rnc";
export const LEGACY_PROVIDER_COMPATIBILITY_DEADLINE =
  Date.parse("2026-08-31T23:59:59-04:00");

export type UserRole =
  | "super_admin"
  | "tenant_admin"
  | "manager"
  | "accountant"
  | "hr"
  | "sales"
  | "technician"
  | "supervisor"
  | "cashier";

export interface AuthoritativeTenantMembership {
  tenantId: string;
  tenantName: string;
  businessType: string;
  role: Exclude<UserRole, "super_admin">;
  permissions: string[];
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeTenantId(value: unknown): string {
  if (typeof value !== "string") {
    throw new Error("La membresía no contiene un tenant válido.");
  }
  const normalized = value.trim().toLowerCase();
  if (!TENANT_ID_PATTERN.test(normalized)) {
    throw new Error("La membresía no contiene un tenant válido.");
  }
  return normalized;
}

function normalizeRole(
  value: unknown,
): Exclude<UserRole, "super_admin"> {
  if (typeof value !== "string") {
    throw new Error("La membresía no contiene un rol válido.");
  }

  const roleMap: Record<string, Exclude<UserRole, "super_admin">> = {
    super_admin: "tenant_admin",
    admin: "tenant_admin",
    tenant_admin: "tenant_admin",
    manager: "manager",
    accountant: "accountant",
    contador: "accountant",
    hr: "hr",
    sales: "sales",
    vendedor: "sales",
    technician: "technician",
    supervisor: "supervisor",
    cashier: "cashier",
    cajero: "cashier",
  };
  const normalized = roleMap[value.trim().toLowerCase()];
  if (!normalized) {
    throw new Error("La membresía contiene un rol no reconocido.");
  }
  return normalized;
}

function normalizePermissions(value: unknown): string[] {
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) {
    throw new Error("La membresía contiene permisos inválidos.");
  }
  return [...new Set(value.map((item) => item.trim()).filter(Boolean))];
}

function normalizeMembershipRow(
  value: unknown,
  includePermissions: boolean,
): AuthoritativeTenantMembership {
  if (!isRecord(value)) {
    throw new Error("La respuesta de membresías no es válida.");
  }

  const tenantName =
    typeof value.tenant_name === "string" ? value.tenant_name.trim() : "";
  if (!tenantName) {
    throw new Error("La membresía no contiene el nombre del tenant.");
  }

  const businessType =
    typeof value.business_type === "string" && value.business_type.trim()
      ? value.business_type.trim()
      : "general";

  return {
    tenantId: normalizeTenantId(value.tenant_id),
    tenantName,
    businessType,
    role: normalizeRole(value.membership_role),
    permissions: includePermissions
      ? normalizePermissions(value.permissions)
      : [],
  };
}

export function normalizeAuthoritativeMemberships(
  value: unknown,
): AuthoritativeTenantMembership[] {
  if (!Array.isArray(value)) {
    throw new Error("La respuesta de membresías no es válida.");
  }

  const memberships = value.map((row) => normalizeMembershipRow(row, true));
  const seen = new Set<string>();
  for (const membership of memberships) {
    if (seen.has(membership.tenantId)) {
      throw new Error("La identidad contiene membresías duplicadas.");
    }
    seen.add(membership.tenantId);
  }
  return memberships;
}

export function normalizeSelectedTenant(
  value: unknown,
  memberships: AuthoritativeTenantMembership[],
): AuthoritativeTenantMembership {
  if (!Array.isArray(value) || value.length !== 1) {
    throw new Error("La empresa seleccionada no pudo autorizarse.");
  }

  const selected = normalizeMembershipRow(value[0], false);
  const authoritative = memberships.filter(
    (membership) => membership.tenantId === selected.tenantId,
  );
  if (
    authoritative.length !== 1 ||
    authoritative[0].tenantName !== selected.tenantName ||
    authoritative[0].businessType !== selected.businessType ||
    authoritative[0].role !== selected.role
  ) {
    throw new Error("La empresa seleccionada no coincide con la membresía.");
  }
  return authoritative[0];
}

export function selectAuthoritativeMembership(
  memberships: AuthoritativeTenantMembership[],
  preferredTenantId: string | null,
): AuthoritativeTenantMembership {
  if (preferredTenantId) {
    const preferred = normalizeTenantId(preferredTenantId);
    const selected = memberships.filter(
      (membership) => membership.tenantId === preferred,
    );
    if (selected.length !== 1) {
      throw new Error("La empresa preferida no pertenece a esta identidad.");
    }
    return selected[0];
  }

  if (memberships.length !== 1) {
    throw new Error(
      memberships.length === 0
        ? "La identidad no tiene una empresa activa."
        : "Seleccione explícitamente una empresa.",
    );
  }
  return memberships[0];
}

export function parseProviderAdmin(value: unknown): boolean {
  if (typeof value !== "boolean") {
    throw new Error("La respuesta de identidad del proveedor no es válida.");
  }
  return value;
}

export function hasLegacyProviderClaim(
  appMetadata: unknown,
): boolean {
  if (!isRecord(appMetadata)) return false;
  return (
    appMetadata.is_superadmin === true ||
    appMetadata.role === "super_admin"
  );
}

export function shouldUseLegacyProviderCompatibility(
  appMetadata: unknown,
  enabled: boolean,
  now = Date.now(),
): boolean {
  return (
    enabled &&
    now <= LEGACY_PROVIDER_COMPATIBILITY_DEADLINE &&
    hasLegacyProviderClaim(appMetadata)
  );
}
